import React from 'react'
import { cn } from '../../utils'

/**
 * Input component with adaptive design and validation states
 * Supports various input types with consistent styling
 */

const Input = React.forwardRef(({
  className,
  type = 'text',
  adaptive = true,
  state = 'default',
  leftIcon,
  rightIcon,
  error,
  helperText,
  label,
  required = false,
  ...props
}, ref) => {
  const stateVariants = {
    default: 'border-border-primary focus:border-primary-500 focus:ring-primary-500',
    error: 'border-error-500 focus:border-error-500 focus:ring-error-500',
    success: 'border-success-500 focus:border-success-500 focus:ring-success-500',
    disabled: 'border-border-primary bg-neutral-50 cursor-not-allowed opacity-50'
  }
  
  const inputId = props.id || `input-${Math.random().toString(36).substr(2, 9)}`
  const hasError = error || state === 'error'
  
  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className={cn(
            'block font-medium text-text-primary mb-2',
            adaptive ? 'text-adaptive-sm' : 'text-sm'
          )}
        >
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-text-tertiary">
              {leftIcon}
            </span>
          </div>
        )}
        
        <input
          type={type}
          id={inputId}
          ref={ref}
          className={cn(
            // Base input styles
            'w-full rounded-md border bg-white px-3 py-2',
            'focus:outline-none focus:ring-1 focus:ring-offset-0',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'placeholder:text-text-tertiary',
            
            // State styles
            stateVariants[hasError ? 'error' : state],
            
            // Adaptive styles
            adaptive && [
              'text-adaptive-base',
              'p-adaptive-sm',
              'lg:hover:border-border-secondary'
            ],
            
            // Icon spacing
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            
            // Transitions
            'transition-colors duration-200',
            
            className
          )}
          {...props}
        />
        
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span className={cn(
              hasError ? 'text-error-500' : 'text-text-tertiary'
            )}>
              {rightIcon}
            </span>
          </div>
        )}
      </div>
      
      {(error || helperText) && (
        <p className={cn(
          'mt-2',
          adaptive ? 'text-adaptive-xs' : 'text-xs',
          hasError ? 'text-error-500' : 'text-text-secondary'
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  )
})

Input.displayName = 'Input'

export { Input }